import dynamic from 'next/dynamic'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'

const MapText = dynamic(() => import('../../components').then(mod => mod.MapText))
const MediaText = dynamic(() => import('../../components').then(mod => mod.MediaText))
const LocationMap = dynamic(() => import('../../components').then(mod => mod.LocationMap))
const Heading = dynamic(() => import('../../components').then(mod => mod.Heading))
const HeroCareers = dynamic(() => import('../../components').then(mod => mod.HeroCareers))
const MainContainer = dynamic(() => import('../../components').then(mod => mod.MainContainer))
const ThreeByThreeGrid = dynamic(() => import('../../components').then(mod => mod.ThreeByThreeGrid))

import { isObjectEmpty } from '../../utils'
import stringToSlug from '../../utils/slugify'
import getGlobalData from '../../services/cacheGlobalData'
import { PAGE_CAREERS } from '../../services/queries/pages'
import { ALL_LOCATIONS_MAP } from '../../services/queries/locationsMap'
import { getAllJobs, fetchPageByGql } from '../../services'
import { STATIC_PAGE_WP_IDS, SLUGS } from '../../utils/siteConfig'
import { scrollToTargetAdjusted } from '../../utils'
import useLengthOfLongestWord from '../../hooks/useLengthOfLongestWord'
import useIntroEnded from '../../hooks/useIntroEnded'

/**
 * Fixed Page - Careers
 */
export default function Careers({ pageData, jobsData, globalData, locationsMapData }) {
  const tl = useRef()
  const headingRef = useRef()
  const { introEnded } = useIntroEnded()

  const header = pageData?.signaturePageCareersFields?.header
  const benefits = pageData?.signaturePageCareersFields?.benefits
  const openPositions = pageData?.signaturePageCareersFields?.openPositions
  const whereWeBuild = pageData?.signaturePageCareersFields?.whereWeBuild
  const statistics = pageData?.signaturePageCareersFields?.statistics
  const joinUs = pageData?.signaturePageCareersFields?.joinUs
  const locations = pageData?.signaturePageCareersFields?.locations
 
  const hLengthMax = useLengthOfLongestWord(header?.title)

  useEffect(() => {
    if (introEnded) {
      tl.current = gsap
        .timeline({ defaults: { duration: 1, ease: 'bigd-ease-out' } })
        .addLabel('init')
    }
  }, [introEnded])

  return (
    <MainContainer>
      {/* SECTION A - HERO - Careers */}
      <HeroCareers
        heroHeading={header?.title}
        heroImage={header?.image}
        heroVideo={header?.video?.mediaItemUrl}
        subHeading={header?.headline}
        description={header?.description}
        cta={{ label: header?.link?.label, url: header?.link?.url }}
        cta2={{ label: header?.link2?.label, url: header?.link2?.url }}
      />

      {/* SECTION B - Benefits */}
      <ThreeByThreeGrid
        heading={benefits?.title}
        subHeading={benefits?.headline}
        benefitsArray={benefits?.items}
      />
      
      <MapText
        heading={whereWeBuild?.title}
        subHeading={whereWeBuild?.headline}
        subHeadingSecondary={whereWeBuild?.subtitle}
        description={whereWeBuild?.description}
        cta={whereWeBuild?.link}
      >
        <LocationMap
          title={locations?.title}
          content={{
            title: locations?.content?.title,
            subtitle: locations?.content?.subtitle1,
            text: locations?.content?.text,
            legend1: locations?.content?.legend1,
            legend2: locations?.content?.legend2,
            legend3: locations?.content?.legend3,
            legend4: locations?.content?.legend4,
          }}
          data={locationsMapData}
          unlicensedStates={globalData.bigdGlobalSettings.unlicensed_states}
          whereWeWork={globalData.bigdGlobalSettings.where_we_work}
        />
      </MapText>
    </MainContainer>
  )
}

export async function getStaticProps({ preview = false }) {
  const query = PAGE_CAREERS
  const params = { pageId: STATIC_PAGE_WP_IDS?.careers }

  const pageData = await fetchPageByGql(query, params, preview)
  const globalData = await getGlobalData()
  
  const locationsMapData = await fetchPageByGql(ALL_LOCATIONS_MAP, {}, preview)

  return isObjectEmpty(pageData)
    ? { notFound: true } // returns custom 404 page
    : { props: { preview, pageData, globalData, locationsMapData } }
}
